import { useMemo } from 'react';
import { simulateLoan } from '../mock';

/**
 * Wraps simulateLoan in memoisation with a compact dependency list.
 * - `current`: full simulation with advanced options + prepayments applied
 * - `baseline`: same as current but with prepayments/extraEmi stripped (used
 *               by the prepayment "you save" panel)
 * - `plain`: same but with ADVANCED stripped too (used by the advanced-options
 *            panel to show the "actual EMI" vs "advance-options EMI" delta)
 */
export default function useLoanSimulation({
  loanAmount,
  interestRate,
  tenureMonths,
  interestMethod,
  moratorium,
  prepayments,
  extraEmi,
  advanced,
}) {
  const morKey = `${moratorium.enabled}|${moratorium.months}|${moratorium.type}`;
  const prepayKey = prepayments.map((p) => `${p.month}:${p.amount}`).join(',');
  const advKey = JSON.stringify(advanced || {});

  const current = useMemo(
    () =>
      simulateLoan({
        principal: loanAmount,
        annualRate: interestRate,
        tenureMonths,
        moratorium,
        prepayments,
        extraEmi,
        interestMethod,
        advanced,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loanAmount, interestRate, tenureMonths, interestMethod, morKey, prepayKey, extraEmi, advKey]
  );

  const baseline = useMemo(
    () =>
      simulateLoan({
        principal: loanAmount,
        annualRate: interestRate,
        tenureMonths,
        moratorium,
        prepayments: [],
        extraEmi: 0,
        interestMethod,
        advanced,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loanAmount, interestRate, tenureMonths, interestMethod, morKey, advKey]
  );

  const plain = useMemo(
    () =>
      simulateLoan({
        principal: loanAmount,
        annualRate: interestRate,
        tenureMonths,
        moratorium,
        prepayments: [],
        extraEmi: 0,
        interestMethod,
        advanced: {},
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loanAmount, interestRate, tenureMonths, interestMethod, morKey]
  );

  return { current, baseline, plain };
}
